<!--
 * @Author: Victor
 * @Date: 2023-12-27 19:08:08
 * @LastEditors: Merlin
 * @LastEditTime: 2025-06-20 13:12:24
-->
<template>
    <div class="q-mt-md">
        <ClientOnly>
            <div
                class="full-width h-[80px] grid gap-2 items-center justify-center text-center"
                :class="showSwitch ? 'grid-cols-7' : 'grid-cols-6'"
            >
                <!-- 下注金額输入框 -->
                <CasinoBetOptionSectionAmountOption
                    :bet-amount-modal-value="betAmountModalValue"
                    :sound="sound"
                    :is-submiting="isSubmiting || amountDisabled"
                    :class="showSwitch ? 'col-span-4' : 'col-span-3'"
                    @update:bet-amount-modal-value="emit('update:betAmountModalValue', $event)"
                    :gameName="joinStateStore.gameName"
                    :changeAmount="changeAmount"
                    :currency="currency"
                />
                <!-- 
                    左右
                    分界
                -->
                <div class="bet-btn-group !w-auto col-span-3 relative">
                    <!-- ↓ 在 stairs 游戏中不显示 -->
                    <div class="mode-switch"
                        v-if="showSwitch" 
                    >
                        <ButtonGrayButton
                            :blue="!isAuto"
                            :disabled="isSubmiting || canBet"
                            @click="changeMode(false)"
                        >
                            {{ t.Manaual }}
                        </ButtonGrayButton>
                        <ButtonGrayButton
                            :blue="isAuto"
                            :disabled="isSubmiting || canBet"
                            @click="changeMode(true)"
                        >
                            {{ t.Auto }}
                        </ButtonGrayButton>
                    </div>
                    <!-- ↓ 自动+动手模式的2个按钮 -->
                    <div v-if="$slots['bet-button']">
                        <slot name="bet-button"></slot>
                    </div>
                    <template v-else>
                        <ButtonBet
                            v-if="!isAuto"
                            :is-submiting="isSubmiting"
                            :bet-submit="betSubmit"
                            class="!px-0"
                            :class="btnColorMode"
                            @update:is-submiting="$emit('update:isSubmiting', $event)"
                            :expected-reward="expectedReward"
                            :expectedRewardLabel="expectedRewardLabel"
                            :disabled="normalBtnDisabled || isSubmiting"
                            :label="normalBtnLable"
                        />
                        <ButtonBetAuto
                            :isSubmiting="isSubmiting"
                            v-else
                            :auto-bet-tool-kit="autoBetToolKit"
                            class="!px-0"
                            :class="btnColorMode"
                            :disabled="autoBtnDisabled"
                        />
                    </template>
                    <!-- 用于触发预警dom(triple、keno、roulette游戏中) -->
                    <slot v-if="$slots['button-mask']" name="button-mask"></slot>
                </div>
            </div>
        </ClientOnly>
    </div>
</template>

<script lang="ts" >
export default defineComponent({name: 'BetOptionSection'});
</script>

<script setup lang="ts">
import useTranslation from '~/i18n/useTransLation';
import type { useBettingToolkit } from '~/common/js/bettingToolkit';
import { useAutoBetToolKit } from '~/common/js/bettingToolkit/auto';
import { usePlinkoStore } from '~/stores/cansino/plinko';
import { useJoinStateStore } from '~/stores/cansino/joinState';

const plinkoStore = usePlinkoStore();
const joinStateStore = useJoinStateStore();
const t = useTranslation();

const props = defineProps({
    betAmountModalValue: {
        // 投注金額
        type: [Number, String],
        required: true,
    },
    isSubmiting: {
        // 用於防止重複點擊,以及自動投注持續化條件
        type: Boolean,
        required: true,
    },
    betSubmit: {
        // 投注方法，在 mini 已经改造为非 required 参数
        type: Function as PropType<() => Promise<any>>,
    },
    isAuto: {
        // 是否為自動
        type: Boolean,
        required: true,
    },
    expectedReward: {
        // 預期獎勵
        type: Number,
        default: 0,
    },
    expectedRewardLabel: {
        type: String,
    },
    sound: {
        type: Boolean,
        default: void 0, // 这里不使用 false 做为默认，以便于识别是否有传入值 
    },
    autoBetToolKit: {
        // 自動投注工具包
        type: Object as PropType<ReturnType<typeof useAutoBetToolKit>>,
        // required: true, // blackjack 不需要传这项
    },
    betTookit: {
        // 投注工具包
        type: Object as PropType<ReturnType<typeof useBettingToolkit>>,
        // required: true, // blackjack 不需要传这项（其它游戏传了这项后交给子组件，但子组件也没调用它，疑似历史遗漏的未清理项） 
    },
    isShowExpactedReward: {
        type: Boolean,
        default: false,
    },
    amountDisabled: {
        type: Boolean,
        default: false,
    },
    normalBtnLable: {
        type: String,
    },
    normalBtnDisabled: {
        type: Boolean,
        default: false,
    },
    autoBtnDisabled: {
        type: Boolean,
        default: false,
    },
    showSwitch: { // 是否显示【自动/自动】的按钮
        type: Boolean,
        default: true, // 默认显示 
    },
    disabledSwitch: { // 是否禁用【自动/自动】的按钮，目前 tower 使用 
        type: Boolean,
        default: false,
    },
    btnColorMode: {
        type: String,
        default: '',
    },
    changeAmount: {
        type: Function, // roulette 专用 
    },
    currency: {
        type: String, // crash 专用 
        default: '',
    },
});
const emit = defineEmits(['stopAuto', 'update:betAmountModalValue', 'update:isSubmiting', 'changeMode']);

const changeMode = (isAutoMode)=>{
    // ↓ 上报新值，由父组件打开窗口 
    emit('changeMode', !!isAutoMode);
};

/**
 * plinko游戏(joinStateStore.gameName === 'plinkoGame'),
 * 监听canBet,plinkoStore.canBet = false则禁止tab切换;
 * 或
 * 如果游戏区有小球(plinkoStore.stageHasBall)，则禁止tab切换。
 */
const canBet = computed(() => {
    const bPlinko = joinStateStore.gameName === 'plinkoGame';
    if ((bPlinko && !plinkoStore.canBet) || (bPlinko && plinkoStore.stageHasBall)){
        return true;
    }
    return !!props.disabledSwitch;
});

</script>

<style  scoped lang="scss">
.bet-btn-group{
    // grid-column: span 2 / span 2;
    height: 100%;
    display: flex;
    & > *{
        height: 100%;
        flex: 1;
    }
}

.mode-switch{
    flex: 0;
    padding-right: 8px;
    position: relative;
    z-index: 2; // 抬高，一定要高于 .button-mask 
    & > button{
        min-width: 40px;
        padding-left: 4px; // 旧值 8px 
        padding-right: 4px; // 旧值 8px 
        font-size: 11px;
        letter-spacing: -0.5px;
        // ↑ 以上属性的设定意在收缩按钮的宽度，以便让按钮的文字显示完整 
        height: calc(50% - 4px); 
        width: 100%;
        &:first-child{
            margin-bottom: 8px;
        }
    }
}

</style>

<style lang="scss">
.button-mask {
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 100%; // 顶到最左侧，最左侧的部分会被遮罩，无妨 
}

</style>
